// lib
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import NewsFeed from 'src/containers/smart_components/feeds/news_feed'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'

// constants & interfaces
import { FEED_DEFAULT_PER_PAGE, FEED_TYPE_NEWS, FEED_WITH_GROUPED_POSTS_DEFAULT_PER_PAGE } from 'src/constants/feed'
import {
  DEFAULT_OG_URL,
  DEFAULT_ROBOTS,
  NEWSFEED_DESCRIPTION_ADDITIONAL_PAGE,
  NEWSFEED_DESCRIPTION_FIRST_PAGE,
  NEWSFEED_OG_IMAGE,
  NEWSFEED_TITLE_ADDITIONAL_PAGE,
  NEWSFEED_TITLE_FIRST_PAGE
} from 'src/constants/meta_tags'
import { NEWS_FEED_PARTIAL } from 'src/constants/urls'
import { textResources } from 'src/lang/de'

// helpers
import { useNewsfeed } from '@lopo-tech/lopo-api'
import { PREVIEW_IMAGE_HEIGHT, PREVIEW_IMAGE_WIDTH } from 'src/constants/image'
import { useFeedWithPagination } from 'src/hooks_shared/use_feed_pagination'
import { useIsPostCreateOpen, usePageTracking } from 'src/pages/hooks/hooks'
import { useGetAppLocationShape, useGetBoundingBox } from 'src/reducers/app-state/app-state_hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { getPageTitle } from 'src/utils/page_title/page_title'
import { LayoutFeaturedContainer } from '../layout/layout_featured_container'
import { createMarkersList } from '../utils/feeds'

const NewsfeedPage: React.FC<RouteComponentProps> = ({ match }) => {
  const locationShape = useGetAppLocationShape()

  const user = useSelectUser()
  const isPostCreateOpen = useIsPostCreateOpen(match)
  const { page, getPageUrl } = useFeedWithPagination()
  const boundingBox = useGetBoundingBox()

  const { data, loading, fetchMore } = useNewsfeed({
    variables: {
      page,
      perPage: FEED_DEFAULT_PER_PAGE,
      boundingBox: boundingBox!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      width: PREVIEW_IMAGE_WIDTH,
      height: PREVIEW_IMAGE_HEIGHT
    },
    skip: !locationShape || !boundingBox
  })

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (data?.newsfeed.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: data.newsfeed.pageInfo.nextPage } })
    }
  }, [fetchMore, data?.newsfeed.pageInfo.nextPage, loadingRef])
  const totalPages = data?.newsfeed.pageInfo.totalPages

  const canonicalUrlPrefix = `${DEFAULT_OG_URL}${locationShape?.slug}${NEWS_FEED_PARTIAL}`

  const startIndex = (page - 1) * FEED_WITH_GROUPED_POSTS_DEFAULT_PER_PAGE + 1
  const endIndex = page * FEED_WITH_GROUPED_POSTS_DEFAULT_PER_PAGE

  const canonicalUrl = page <= 1 ? canonicalUrlPrefix : canonicalUrlPrefix + '?page=' + page
  const title =
    locationShape
      ? page <= 1 || !totalPages
        ? NEWSFEED_TITLE_FIRST_PAGE(locationShape.name)
        : NEWSFEED_TITLE_ADDITIONAL_PAGE(locationShape.name, page, totalPages)
      : undefined
  const description =
    locationShape
      ? page <= 1
        ? NEWSFEED_DESCRIPTION_FIRST_PAGE(locationShape.name)
        : NEWSFEED_DESCRIPTION_ADDITIONAL_PAGE(startIndex, endIndex, locationShape.name)
      : undefined

  const items = useMemo(
    () => data?.newsfeed.items?.flatMap(g => g.items.map(i => i.contentItem)) ?? [],
    [data?.newsfeed.items]
  )

  const markers = createMarkersList(items)

  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set())

  const onVisibleChange = useCallback((id: string, inView: boolean) => {
    setVisibleIds(prev => {
      const next = new Set(prev)
      if (inView) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const markersInView = useMemo(
    () => markers.filter(m => visibleIds.has(m.id)),
    [markers, visibleIds]
  )

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags
        canonical={canonicalUrl}
        stripParamsFromCanonicalUrl={false}
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        robots={DEFAULT_ROBOTS}
        og_image={NEWSFEED_OG_IMAGE}
        og_url={document.location.toString()}
        title={title}
        first_page={getPageUrl(1)}
        last_page={totalPages ? getPageUrl(totalPages) : undefined}
        next_page={page && totalPages && page < totalPages ? getPageUrl(page + 1) : undefined}
        prev_page={page && page > 1 ? getPageUrl(page - 1) : undefined}
      />
      <LayoutFeaturedContainer
        markers={markersInView}
        locationShape={locationShape}
        activeNavigationItem={FEED_TYPE_NEWS}
        pageTitle={
          <StandardPageTitle
            text={getPageTitle(
              textResources.pageTitles.newsfeedSimple,
              textResources.pageTitles.newsfeed
            )}
            showLocation
          />
        }
        withHeaderMargin
      >
        <NewsFeed
          isPostCreateOpen={isPostCreateOpen}
          getNextPage={getNextPage}
          loading={loading}
          newsfeedData={data?.newsfeed}
          showNewPostButton={!!page && page > 1}
          onVisibleChange={onVisibleChange}
        />
      </LayoutFeaturedContainer>
    </>
  )
}

export default NewsfeedPage
